import { STORAGE_KEYS, getJSON, setJSON } from '#/shared/lib/storage'

/**
 * iPhone-keyboard-style local learning: per-agent word-pair (bigram) counts
 * mined from the messages they send. Predictions are instant, free and never
 * leave the device — in the console this is the whole of smart compose, with
 * no model call behind it. Only counts are stored, never message bodies.
 */

/** One observed "prev → next" pair: count + the display casing last seen. */
interface GramEntry {
  c: number
  d: string
}

interface ModelData {
  v: 1
  /** prevWordLower → nextWordLower → entry. */
  grams: Record<string, Record<string, GramEntry>>
  /** ISO watermark: outbound messages at/before this are already counted, so
   *  re-opening a thread never double-learns its history. */
  learnedUntil: string
}

/** Hard size cap: ~5k pairs ≈ 150 KB serialized. On overflow every count is
 *  halved and zeroed entries drop — stale vocabulary decays away first. */
const MAX_PAIRS = 5000
/** Cap successors kept per word; the lowest-count one is evicted when full. */
const MAX_FANOUT = 40
/** A pair must have been typed twice before it's trusted for prediction. */
const MIN_COUNT = 2
/** Minimum typed prefix before mid-word completion kicks in. */
const MIN_PREFIX = 2

function emptyData(): ModelData {
  return { v: 1, grams: {}, learnedUntil: '' }
}

/** Words worth learning: strip edge punctuation, drop anything with digits
 *  (phone numbers, prices, VINs) or URLs — noise for next-word prediction. */
function tokenize(text: string): string[] {
  return text
    .split(/\s+/)
    .map((word) => word.replace(/^[^\p{L}\p{N}']+|[^\p{L}\p{N}']+$/gu, ''))
    .filter(
      (word) =>
        word !== '' && !/\d/.test(word) && !word.toLowerCase().includes('http'),
    )
}

function topEntry(
  bucket: Record<string, GramEntry>,
  accept: (key: string) => boolean,
): { key: string; entry: GramEntry } | null {
  let best: { key: string; entry: GramEntry } | null = null
  for (const [key, entry] of Object.entries(bucket)) {
    if (!accept(key)) continue
    if (entry.c >= MIN_COUNT && (best === null || entry.c > best.entry.c)) {
      best = { key, entry }
    }
  }
  return best
}

export class SmartComposeLocalModel {
  private data: ModelData

  constructor(private readonly storageKey: string) {
    this.data = getJSON<ModelData>(storageKey) ?? emptyData()
  }

  /** Count the word pairs of one sent message into the model. */
  learn(text: string): void {
    const words = tokenize(text)
    for (let i = 0; i < words.length - 1; i++) {
      const prev = words[i].toLowerCase()
      const next = words[i + 1]
      const bucket = (this.data.grams[prev] ??= {})
      const key = next.toLowerCase()
      if (key in bucket) {
        bucket[key].c += 1
        bucket[key].d = next
        continue
      }
      const keys = Object.keys(bucket)
      if (keys.length >= MAX_FANOUT) {
        let lowest = keys[0]
        for (const k of keys) if (bucket[k].c < bucket[lowest].c) lowest = k
        if (bucket[lowest].c > 1) continue
        delete bucket[lowest]
      }
      bucket[key] = { c: 1, d: next }
    }
    this.pruneIfNeeded()
    this.save()
  }

  /**
   * Learn every not-yet-counted agent-written message and advance the
   * watermark. Skips AI-generated and failed sends — the model should carry
   * this agent's own voice, not the bot's or a message that never left.
   */
  learnMessages(messages: ReadonlyArray<{ at: string; text: string }>): void {
    const fresh = messages
      .filter((message) => message.at > this.data.learnedUntil)
      .sort((a, b) => a.at.localeCompare(b.at))
    if (fresh.length === 0) return
    for (const message of fresh) this.learn(message.text)
    this.data.learnedUntil = fresh[fresh.length - 1].at
    this.save()
  }

  /**
   * Instant next-word prediction for the draft, iPhone-keyboard style:
   * mid-word ⇒ finish the word (biased by the previous word), after a space ⇒
   * the most likely next word, chaining a second word only when it's strong.
   * Null = no confident local answer (caller falls back to the API).
   */
  predict(draft: string): string | null {
    const trimmed = draft.trim()
    if (trimmed === '') return null
    const rawWords = trimmed.split(/\s+/)
    if (/\s$/.test(draft)) return this.predictNextWord(rawWords)
    return this.predictWordEnd(rawWords)
  }

  private bucketFor(prev: string): Record<string, GramEntry> | undefined {
    return prev in this.data.grams ? this.data.grams[prev] : undefined
  }

  private predictNextWord(rawWords: string[]): string | null {
    const bucket = this.bucketFor(rawWords[rawWords.length - 1].toLowerCase())
    if (!bucket) return null
    const best = topEntry(bucket, () => true)
    if (!best) return null
    let suggestion = best.entry.d
    // Chain one more word only when clearly established (typed 3+ times).
    const nextBucket = this.bucketFor(best.key)
    if (nextBucket) {
      const chained = topEntry(nextBucket, () => true)
      if (chained && chained.entry.c >= MIN_COUNT + 1) {
        suggestion += ` ${chained.entry.d}`
      }
    }
    return suggestion
  }

  private predictWordEnd(rawWords: string[]): string | null {
    const partial = rawWords[rawWords.length - 1]
      .replace(/^[^\p{L}\p{N}']+/u, '')
      .toLowerCase()
    if (partial.length < MIN_PREFIX) return null
    const completes = (key: string) =>
      key.startsWith(partial) && key.length > partial.length
    // Prefer completions seen after the actual previous word…
    if (rawWords.length > 1) {
      const bucket = this.bucketFor(rawWords[rawWords.length - 2].toLowerCase())
      if (bucket) {
        const best = topEntry(bucket, completes)
        if (best) return best.entry.d.slice(partial.length)
      }
    }
    // …else the best completion across the whole vocabulary.
    const best = this.completeAcrossVocabulary(completes)
    return best ? best.slice(partial.length) : null
  }

  /** Best-scoring word matching `completes`, summed across every bucket. */
  private completeAcrossVocabulary(
    completes: (key: string) => boolean,
  ): string | null {
    const totals: Record<string, GramEntry> = {}
    for (const bucket of Object.values(this.data.grams)) {
      for (const [key, entry] of Object.entries(bucket)) {
        if (!completes(key)) continue
        const total = (totals[key] ??= { c: 0, d: entry.d })
        total.c += entry.c
      }
    }
    const best = topEntry(totals, () => true)
    return best ? best.entry.d : null
  }

  /** Decay on overflow: halve every count, drop the zeroed ones. Keeps the
   *  model inside the size cap and lets abandoned phrasing age out. */
  private pruneIfNeeded(): void {
    let pairs = 0
    for (const bucket of Object.values(this.data.grams)) {
      pairs += Object.keys(bucket).length
    }
    if (pairs <= MAX_PAIRS) return
    for (const [prev, bucket] of Object.entries(this.data.grams)) {
      for (const [key, entry] of Object.entries(bucket)) {
        entry.c = Math.floor(entry.c / 2)
        if (entry.c === 0) delete bucket[key]
      }
      if (Object.keys(bucket).length === 0) delete this.data.grams[prev]
    }
  }

  private save(): void {
    setJSON(this.storageKey, this.data)
  }
}

const cache = new Map<string, SmartComposeLocalModel>()

/** The (cached) local model for one agent — keyed per user id so accounts
 *  sharing a browser never learn from each other. */
export function localModelFor(userId: string): SmartComposeLocalModel {
  const key = `${STORAGE_KEYS.smartCompose.key}:${userId}`
  let model = cache.get(key)
  if (!model) {
    model = new SmartComposeLocalModel(key)
    cache.set(key, model)
  }
  return model
}
